/**
 * Custom Forms capabilities from user-profiles page_configuration.
 */

import { FORM_ASSIGNMENT_METADATA_KEYS } from './formAssignmentMetadata';

export const CUSTOM_FORMS_SUBMISSION_SCOPES = Object.freeze({
  OWN_PROFILE: 'own_profile_only',
  ASSIGNED_STUDENTS: 'assigned_students',
  ORGANIZATION: 'organization',
  PLATFORM: 'platform'
});

export const CUSTOM_FORMS_SUBMISSION_SCOPE_LABELS = {
  [CUSTOM_FORMS_SUBMISSION_SCOPES.OWN_PROFILE]: 'Own profile only',
  [CUSTOM_FORMS_SUBMISSION_SCOPES.ASSIGNED_STUDENTS]: 'Assigned students (other profiles)',
  [CUSTOM_FORMS_SUBMISSION_SCOPES.ORGANIZATION]: 'Organization (profiles you can open)',
  [CUSTOM_FORMS_SUBMISSION_SCOPES.PLATFORM]: 'Platform-wide'
};

function normalizeCustomFormsSubmissionScope(value) {
  const v = String(value || '').trim();
  if (Object.values(CUSTOM_FORMS_SUBMISSION_SCOPES).includes(v)) return v;
  return CUSTOM_FORMS_SUBMISSION_SCOPES.OWN_PROFILE;
}

/**
 * Staff collaboration capabilities from user-profiles page_configuration.
 * Stored under configuration.collaboration:
 *   chat: { public: {read, write}, internal: {read, write}, private: {read, write, edit} }
 *   flags: { read, create, resolve, reopen, markAddressed, createWorkSample }
 *   admin: { viewAllPrivateMessages }
 */
export function normalizeCollaborationConfig(raw) {
  const c = raw && typeof raw === 'object' ? raw : {};
  const chat = c.chat && typeof c.chat === 'object' ? c.chat : {};
  const flags = c.flags && typeof c.flags === 'object' ? c.flags : {};
  const admin = c.admin && typeof c.admin === 'object' ? c.admin : {};
  const vis = (v) => (v && typeof v === 'object' ? v : {});
  const pub = vis(chat.public);
  const internal = vis(chat.internal);
  const priv = vis(chat.private);
  return {
    chat: {
      public: { read: pub.read === true, write: pub.write === true },
      internal: { read: internal.read === true, write: internal.write === true },
      private: {
        read: priv.read === true,
        write: priv.write === true,
        edit: priv.edit === true
      }
    },
    flags: {
      read: flags.read === true,
      create: flags.create === true,
      resolve: flags.resolve === true,
      reopen: flags.reopen === true,
      markAddressed: flags.markAddressed === true,
      createWorkSample: flags.createWorkSample === true
    },
    admin: {
      viewAllPrivateMessages: admin.viewAllPrivateMessages === true
    }
  };
}

function collaborationHasAnyCapability(collab) {
  if (!collab) return false;
  const { chat, flags, admin } = collab;
  return (
    chat.public.read ||
    chat.public.write ||
    chat.internal.read ||
    chat.internal.write ||
    chat.private.read ||
    chat.private.write ||
    flags.read ||
    flags.create ||
    flags.resolve ||
    flags.reopen ||
    flags.markAddressed ||
    flags.createWorkSample ||
    admin.viewAllPrivateMessages
  );
}

export function getProfileFormPermissions(pageConfig) {
  const cfg = pageConfig?.configuration || {};
  const hasEditDraftKey = Object.prototype.hasOwnProperty.call(cfg, 'can_edit_draft');
  const canEditSubmission = cfg.can_edit_submission === true;
  const canCreateSubmission = cfg.can_create_submission === true;

  let canEditDraft;
  if (hasEditDraftKey) {
    canEditDraft = cfg.can_edit_draft === true;
  } else if (canEditSubmission) {
    canEditDraft = true;
  } else {
    canEditDraft = canCreateSubmission;
  }

  return {
    canViewForm: cfg.can_view_form === true,
    canCreateSubmission,
    canViewSubmission: cfg.can_view_submission === true,
    canEditDraft,
    canEditSubmission,
    canDeleteSubmission: cfg.can_delete_submission === true,
    canViewQuestionAnswers: cfg.can_view_question_answers === true,
    canUpdatePoints: cfg.can_update_points === true,
    collaboration: normalizeCollaborationConfig(cfg.collaboration),
    submissionScope: normalizeCustomFormsSubmissionScope(cfg.custom_forms_submission_scope)
  };
}

export function isCustomFormsScopeAllowed(perms, ctx = {}) {
  if (!perms) return false;
  const scope = perms.submissionScope || CUSTOM_FORMS_SUBMISSION_SCOPES.OWN_PROFILE;
  const actorId = String(ctx.actorUserId || '');
  const profileId = String(ctx.profileUserId || '');
  if (!actorId || !profileId) return false;

  switch (scope) {
    case CUSTOM_FORMS_SUBMISSION_SCOPES.PLATFORM:
      return true;
    case CUSTOM_FORMS_SUBMISSION_SCOPES.ORGANIZATION:
      if (actorId === profileId) return true;
      if (ctx.sameOrganization === true) return true;
      return ctx.viewingOtherProfile === true;
    case CUSTOM_FORMS_SUBMISSION_SCOPES.ASSIGNED_STUDENTS:
      return actorId !== profileId;
    case CUSTOM_FORMS_SUBMISSION_SCOPES.OWN_PROFILE:
    default:
      return actorId === profileId;
  }
}

export function canOpenProfileForm(perms, ctx = {}) {
  return perms?.canViewForm === true && isCustomFormsScopeAllowed(perms, ctx);
}

export function canCreateProfileFormSubmission(perms, ctx = {}) {
  return perms?.canCreateSubmission === true && isCustomFormsScopeAllowed(perms, ctx);
}

export function canEditProfileFormDraft(perms, ctx = {}) {
  return perms?.canEditDraft === true && isCustomFormsScopeAllowed(perms, ctx);
}

export function canEditSubmittedProfileForm(perms, ctx = {}) {
  return perms?.canEditSubmission === true && isCustomFormsScopeAllowed(perms, ctx);
}

export function canEditProfileFormAnswers(
  perms,
  ctx = {},
  { hasExistingSubmission = false, isDraft = true } = {}
) {
  if (!isCustomFormsScopeAllowed(perms, ctx)) return false;
  if (!hasExistingSubmission) return perms?.canCreateSubmission === true;
  if (isDraft) return canEditProfileFormDraft(perms, ctx);
  return canEditSubmittedProfileForm(perms, ctx);
}

export function canDeleteProfileFormSubmission(perms, ctx = {}) {
  return perms?.canDeleteSubmission === true && isCustomFormsScopeAllowed(perms, ctx);
}

export function canUpdateProfileFormPoints(perms, ctx = {}) {
  return perms?.canUpdatePoints === true && isCustomFormsScopeAllowed(perms, ctx);
}

export function canSeeProfileFormSubmission(perms, ctx = {}) {
  if (!isCustomFormsScopeAllowed(perms, ctx)) return false;
  return (
    perms?.canViewSubmission === true ||
    canEditProfileFormDraft(perms, ctx) ||
    canEditSubmittedProfileForm(perms, ctx)
  );
}

export function isAssigneeOnProfile(ctx = {}) {
  return (
    ctx.actorUserId != null &&
    ctx.profileUserId != null &&
    String(ctx.actorUserId) === String(ctx.profileUserId)
  );
}

function hasCollaborationPagePermissions(perms) {
  return collaborationHasAnyCapability(perms?.collaboration);
}

export function hasStaffAssignmentAccess(perms, ctx = {}) {
  if (!isCustomFormsScopeAllowed(perms, ctx)) return false;
  return (
    canOpenProfileForm(perms, ctx) ||
    canSeeProfileFormSubmission(perms, ctx) ||
    canEditProfileFormDraft(perms, ctx) ||
    canEditSubmittedProfileForm(perms, ctx) ||
    hasCollaborationPagePermissions(perms)
  );
}

export function canAccessAssignmentCollaboration(perms, ctx = {}) {
  if (!isCustomFormsScopeAllowed(perms, ctx)) return false;
  if (isAssigneeOnProfile(ctx)) return true;
  return hasStaffAssignmentAccess(perms, ctx);
}

/**
 * Effective collaboration capabilities for the actor on this assignment.
 *
 * Staff capabilities come only from page permissions (perms.collaboration).
 * The assignee (student) is governed by the assignment/submission policy cascade —
 * public chat (none|read|write), flag visibility, and mark-addressed.
 */
export function resolveCollaborationCapabilities(perms, ctx = {}, policy = {}) {
  const assignee = isAssigneeOnProfile(ctx);
  const staffAccess = hasStaffAssignmentAccess(perms, ctx);
  const collab = perms?.collaboration || normalizeCollaborationConfig(null);

  if (assignee) {
    const publicWrite = policy[FORM_ASSIGNMENT_METADATA_KEYS.ASSIGNEE_PUBLIC_WRITE] === true;
    const publicRead =
      publicWrite || policy[FORM_ASSIGNMENT_METADATA_KEYS.ASSIGNEE_PUBLIC_READ] !== false;
    const assigneeFlagsRead = policy[FORM_ASSIGNMENT_METADATA_KEYS.ASSIGNEE_FLAGS_READ] !== false;
    const assigneeMarkAddressed =
      policy[FORM_ASSIGNMENT_METADATA_KEYS.ASSIGNEE_MARK_ADDRESSED] === true;

    const chat = {
      public: { canRead: publicRead, canWrite: publicWrite },
      internal: { canRead: false, canWrite: false },
      private: {
        canRead: collab.chat.private.read,
        canWrite: collab.chat.private.write,
        canEdit: collab.chat.private.edit,
        canViewAll: false
      }
    };
    const flags = {
      canRead: assigneeFlagsRead,
      canCreate: false,
      canResolve: false,
      canReopen: false,
      canMarkAddressed: assigneeMarkAddressed,
      canCreateWorkSample: false
    };
    return buildCapabilityResult({ assignee: true, hasAccess: true, chat, flags });
  }

  if (!staffAccess) {
    return buildCapabilityResult({
      assignee: false,
      hasAccess: false,
      chat: {
        public: { canRead: false, canWrite: false },
        internal: { canRead: false, canWrite: false },
        private: { canRead: false, canWrite: false, canEdit: false, canViewAll: false }
      },
      flags: {
        canRead: false,
        canCreate: false,
        canResolve: false,
        canReopen: false,
        canMarkAddressed: false,
        canCreateWorkSample: false
      }
    });
  }

  const chat = {
    public: { canRead: collab.chat.public.read, canWrite: collab.chat.public.write },
    internal: { canRead: collab.chat.internal.read, canWrite: collab.chat.internal.write },
    private: {
      canRead: collab.chat.private.read,
      canWrite: collab.chat.private.write,
      canEdit: collab.chat.private.edit,
      canViewAll: collab.admin.viewAllPrivateMessages
    }
  };
  const flags = {
    canRead: collab.flags.read,
    canCreate: collab.flags.create,
    canResolve: collab.flags.resolve,
    canReopen: collab.flags.reopen,
    canMarkAddressed: collab.flags.markAddressed,
    canCreateWorkSample: collab.flags.createWorkSample
  };
  return buildCapabilityResult({ assignee: false, hasAccess: true, chat, flags });
}

function buildCapabilityResult({ assignee, hasAccess, chat, flags }) {
  const showChat =
    chat.public.canRead ||
    chat.public.canWrite ||
    chat.internal.canRead ||
    chat.internal.canWrite ||
    chat.private.canRead ||
    chat.private.canWrite;
  const showFlags = flags.canRead || flags.canCreate;
  return {
    isAssignee: assignee,
    hasAccess,
    hasStaffAccess: !assignee && hasAccess,
    chat: { ...chat, showButton: showChat },
    flags: { ...flags, showButton: showFlags },
    showChat,
    showFlags
  };
}

/** @deprecated prefer canEditProfileFormAnswers */
export function canEditProfileFormSubmission(perms, hasExistingSubmission, ctx = {}, isDraft = true) {
  return canEditProfileFormAnswers(perms, ctx, { hasExistingSubmission, isDraft });
}

export function buildCustomFormsPermissionContext(authUser, profileUser) {
  const actorUserId = authUser?.id || null;
  const profileUserId = profileUser?.id || null;
  const viewingOtherProfile =
    actorUserId != null &&
    profileUserId != null &&
    String(actorUserId) !== String(profileUserId);
  return {
    actorUserId,
    profileUserId,
    viewingOtherProfile,
    sameOrganization: viewingOtherProfile
  };
}
